import {
  Banner,
  BannerAction,
  BannerTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@aircall/ds";
import { ArrowDownToLine, EllipsisVertical, Info } from "@aircall/react-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FeedbackButton } from "@/components/FeedbackButton/FeedbackButton";
import { HelpButton } from "@/components/HelpButton/HelpButton";
import { ShareLink } from "@/components/ShareLink/ShareLink";
import { useCall } from "@/hooks/useCall/useCall";
import { useHeaderRules } from "@/hooks/useHeaderRules/useHeaderRules";
import { useNoInformationRule } from "@/hooks/useNoInformationRule/useNoInformationRule";
import { useBaseStore } from "@/store/store";
import { Title } from "../Title/Title";
import { HEADER_ACTION_BUTTON_SIZE } from "./Header.constants";
import { HeaderProps } from "./Header.decl";
import { useHandlers } from "./Header.hooks";

const DELAY = 200;

function InfoBanner() {
  const { t } = useTranslation();

  const { shouldDisplayInfoBanner } = useHeaderRules();
  const [showBanner, setShowBanner] = useState(shouldDisplayInfoBanner);

  const handleOnCloseBanner = () => {
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <Banner variant="info">
      <Info />
      <BannerTitle>{t("conversation_lt_five_seconds")}</BannerTitle>
      <BannerAction>
        <Button variant="ghost" size="sm" onClick={handleOnCloseBanner}>
          {t("close")}
        </Button>
      </BannerAction>
    </Banner>
  );
}

function PickActions({ mobile }: HeaderProps) {
  const { t, i18n } = useTranslation();

  // SatisMeter feedback is not translated in Japanese, so hide the button to
  // avoid it looking like a missing translation. See DAT-458.
  const showFeedbackButton = !i18n.language.startsWith("ja");

  const {
    handleOnClickShare,
    handleOnClickDownloadRecording,
    handleOnClickFeeback,
    handleOnClickHelp,
    handleOnClickDownloadTranscript,
  } = useHandlers();

  const audio = useBaseStore((store) => store.audio);
  const { call } = useCall();

  const hasAudio = Boolean(audio);
  const hasTranscript = Boolean(call?.transcription);

  if (mobile) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="ghost" size={HEADER_ACTION_BUTTON_SIZE} />}
        >
          <EllipsisVertical className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom">
          <DropdownMenuItem onClick={handleOnClickShare}>
            {t("copy_link")}
          </DropdownMenuItem>
          {hasAudio && (
            <DropdownMenuItem onClick={handleOnClickDownloadRecording}>
              {t("download_audio")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            disabled={!hasTranscript}
            onClick={handleOnClickDownloadTranscript}
          >
            {t("download_transcript")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleOnClickHelp}>
            {t("help_center")}
          </DropdownMenuItem>
          {showFeedbackButton && (
            <DropdownMenuItem onClick={handleOnClickFeeback}>
              {t("feedback_button")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <ShareLink />
      {(hasAudio || hasTranscript) && (
        <Tooltip>
          {/* `flex` on the trigger wrapper: as a bare block it gets a line box,
              whose strut leaves the button a few px off-centre in the row. */}
          <TooltipTrigger delay={DELAY} render={<div className="flex" />}>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    data-test="download-button"
                    variant="ghost"
                    size={HEADER_ACTION_BUTTON_SIZE}
                  />
                }
              >
                <ArrowDownToLine className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {hasAudio && (
                  <DropdownMenuItem
                    data-test="download-audio-button"
                    onClick={handleOnClickDownloadRecording}
                  >
                    {t("download_audio")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-test="download-transcript-button"
                  disabled={!hasTranscript}
                  onClick={handleOnClickDownloadTranscript}
                >
                  {t("download_transcript")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipTrigger>
          <TooltipContent>{t("export")}</TooltipContent>
        </Tooltip>
      )}
      <HelpButton />
      {showFeedbackButton && (
        <FeedbackButton event="assets_page_feedback_button_clicked" />
      )}
    </div>
  );
}

export function Header({ mobile }: HeaderProps) {
  const { call } = useCall();
  const { hasNoInformation } = useNoInformationRule();

  if (!call) {
    return null;
  }

  // A call with neither a recording nor a transcription has no one we can
  // claim spoke on it: rendering from/to here would name people who never
  // participated (CI-6280, CI-6448), so the speaker line stays hidden.
  const hasParticipants = !!call.from && !!call.to && !hasNoInformation;

  return (
    <div className="flex flex-col">
      <InfoBanner />
      <div
        className="flex items-center justify-between border-b border-border bg-card px-6 py-3"
        data-test="call-details-container"
      >
        <div className="flex items-center">
          {hasParticipants && (
            <Title
              type={call.type}
              from={call.from}
              to={call.to}
              speakers={call.speakers}
            />
          )}
        </div>
        <PickActions mobile={mobile} />
      </div>
    </div>
  );
}
